<script lang="ts">
    import { translate } from "@utils/format";
    import { Modal, Spinner } from "@/components";
    import {
        getCreativesManifest,
        getFormatIcon,
        normalizeCreativeLanguage,
        supportedCreativeLanguages,
        type CreativeLanguage,
        type Creative,
    } from "@utils/creatives/creativesManifest";
    import appStore from "@utils/store";
    import { generateQrDataUrl } from "@utils/creatives/qr";
    import {
        clamp,
        exportCanvas,
        getDefaultLogoPosition,
        getDefaultQrPosition,
        loadImage,
        renderComposition,
        type OverlayElement,
    } from "@utils/creatives/canvasComposer";

    let {
        referralLink = "",
        partnerLogo = "",
        referralCode = "",
        onClose = () => {},
    }: {
        referralLink: string;
        partnerLogo?: string;
        referralCode?: string;
        onClose?: (reason?: unknown) => void;
    } = $props();

    let step = $state<"select" | "edit">("select");
    let selectedCreative = $state<Creative | null>(null);
    const defaultCreativeLanguage = normalizeCreativeLanguage(
        String(appStore.get("language") || "es"),
    );
    let creativeLanguage = $state<CreativeLanguage>(defaultCreativeLanguage);

    let uploadedCreatives = $state<Creative[]>([]);
    let fileInputRef = $state<HTMLInputElement | null>(null);
    let extraInputRef = $state<HTMLInputElement | null>(null);
    const createdObjectUrls: string[] = [];

    let missingTemplateSrc = $state<Record<string, true>>({});
    let selectionCreatives = $derived(
        [
            ...uploadedCreatives,
            ...getCreativesManifest(creativeLanguage),
        ].filter((c) => !missingTemplateSrc[c.src]),
    );

    let loading = $state(false);
    let error = $state("");

    let canvasRef = $state<HTMLCanvasElement | null>(null);
    let previewRef = $state<HTMLDivElement | null>(null);
    let baseImage = $state<HTMLImageElement | null>(null);
    let qrImage = $state<HTMLImageElement | null>(null);
    let logoImage = $state<HTMLImageElement | null>(null);

    let showQr = $state(true);
    let showLogo = $state(false);
    let showExtra = $state(false);

    let qrSizePercent = $state(20);
    let qrX = $state(0);
    let qrY = $state(0);
    let qrSize = $state(200);

    let logoSizePercent = $state(15);
    let logoX = $state(0);
    let logoY = $state(0);
    let logoSize = $state(150);
    let logoH = $state(150);

    let extraImage = $state<HTMLImageElement | null>(null);
    let extraSizePercent = $state(15);
    let extraX = $state(0);
    let extraY = $state(0);
    let extraSize = $state(150);
    let extraH = $state(150);

    let dragging: null | "qr" | "logo" | "extra" = $state(null);
    let resizing: null | "qr" | "logo" | "extra" = $state(null);
    let dragStartClientX = $state(0);
    let dragStartClientY = $state(0);
    let dragStartX = $state(0);
    let dragStartY = $state(0);
    let resizeStartSize = $state(0);

    let previewRaf = 0;
    let zoom = $state(0.5);
    let fitZoom = $state(0.5);
    let zoomAuto = $state(true);
    let manualZoom = $state(0.5);

    function sizeFromPercent(percent: number, creative: Creative): number {
        const shortSide = Math.min(creative.width, creative.height);
        return Math.max(16, Math.floor(shortSide * (percent / 100)));
    }

    function getAspect(img: HTMLImageElement | null): number {
        const w = img?.naturalWidth || 1;
        const h = img?.naturalHeight || 1;
        return h / w;
    }

    function computeHeight(
        width: number,
        img: HTMLImageElement | null,
    ): number {
        return Math.max(16, Math.round(width * getAspect(img)));
    }

    $effect(() => {
        return () => {
            for (const url of createdObjectUrls) {
                try {
                    URL.revokeObjectURL(url);
                } catch {
                    // noop
                }
            }
        };
    });

    $effect(() => {
        // Reset missing-cache when switching language, so we re-evaluate what's available.
        creativeLanguage;
        missingTemplateSrc = {};
    });

    function hideMissingTemplate(src: string) {
        if (!src) return;
        if (missingTemplateSrc[src]) return;
        missingTemplateSrc = {
            ...missingTemplateSrc,
            [src]: true,
        };
    }

    function openFilePicker() {
        fileInputRef?.click();
    }

    function openExtraPicker() {
        extraInputRef?.click();
    }

    function handleFiles(e: Event) {
        const input = e.currentTarget as HTMLInputElement | null;
        const files = input?.files;
        if (!files || files.length === 0) return;

        const next: Creative[] = [];
        const now = Date.now();

        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            if (!file) continue;
            if (!file.type.startsWith("image/")) continue;

            const url = URL.createObjectURL(file);
            createdObjectUrls.push(url);

            next.push({
                id: `uploaded-${now}-${i}`,
                src: url,
                label: file.name,
                recommendedFormat: "square",
                width: 1080,
                height: 1080,
                description: translate(
                    "LANG_CREATIVE_CUSTOM_IMAGE",
                    "Imagen personalizada",
                ),
            });
        }

        if (next.length > 0) {
            uploadedCreatives = [...next, ...uploadedCreatives];
        }

        if (input) input.value = "";
    }

    async function handleExtraFile(e: Event) {
        const input = e.currentTarget as HTMLInputElement | null;
        const file = input?.files?.item(0);
        if (!file || !file.type.startsWith("image/")) {
            if (input) input.value = "";
            return;
        }

        error = "";
        loading = true;

        try {
            const url = URL.createObjectURL(file);
            createdObjectUrls.push(url);
            extraImage = await loadImage(url);
            showExtra = true;

            if (selectedCreative) {
                extraSize = sizeFromPercent(extraSizePercent, selectedCreative);
                extraH = computeHeight(extraSize, extraImage);
                const padding = 48;
                extraX = clamp(padding, 0, selectedCreative.width - extraSize);
                extraY = clamp(padding, 0, selectedCreative.height - extraH);
            }
        } catch (e2) {
            console.error(e2);
            extraImage = null;
            showExtra = false;
            error = translate(
                "LANG_CREATIVE_LOAD_ERROR",
                "No se pudo cargar la creatividad",
            );
        } finally {
            loading = false;
            if (input) input.value = "";
        }
    }

    function syncSizesAndClamp() {
        if (!selectedCreative) return;

        qrSize = sizeFromPercent(qrSizePercent, selectedCreative);
        logoSize = sizeFromPercent(logoSizePercent, selectedCreative);
        extraSize = sizeFromPercent(extraSizePercent, selectedCreative);

        // Preserve aspect ratio for image overlays (QR remains square)
        logoH = computeHeight(logoSize, logoImage);
        extraH = computeHeight(extraSize, extraImage);

        qrX = clamp(qrX, 0, selectedCreative.width - qrSize);
        qrY = clamp(qrY, 0, selectedCreative.height - qrSize);

        logoX = clamp(logoX, 0, selectedCreative.width - logoSize);
        logoY = clamp(logoY, 0, selectedCreative.height - logoH);

        extraX = clamp(extraX, 0, selectedCreative.width - extraSize);
        extraY = clamp(extraY, 0, selectedCreative.height - extraH);
    }

    function getClientPoint(e: MouseEvent | TouchEvent): {
        x: number;
        y: number;
    } {
        if ("touches" in e && e.touches.length > 0) {
            const touch = e.touches.item(0);
            if (touch) return { x: touch.clientX, y: touch.clientY };
        }
        const me = e as MouseEvent;
        return { x: me.clientX, y: me.clientY };
    }

    function startDrag(which: "qr" | "logo", e: MouseEvent | TouchEvent) {
        if (!selectedCreative || !canvasRef) return;
        if (which === "logo" && (!showLogo || !logoImage)) return;
        if (which === "qr" && !showQr) return;

        e.preventDefault();
        resizing = null;
        dragging = which;

        const p = getClientPoint(e);
        dragStartClientX = p.x;
        dragStartClientY = p.y;

        if (which === "qr") {
            dragStartX = qrX;
            dragStartY = qrY;
        } else {
            dragStartX = logoX;
            dragStartY = logoY;
        }
    }

    function startDragExtra(e: MouseEvent | TouchEvent) {
        if (!selectedCreative || !canvasRef) return;
        if (!showExtra || !extraImage) return;

        e.preventDefault();
        resizing = null;
        dragging = "extra";

        const p = getClientPoint(e);
        dragStartClientX = p.x;
        dragStartClientY = p.y;

        dragStartX = extraX;
        dragStartY = extraY;
    }

    function startResize(
        which: "qr" | "logo" | "extra",
        e: MouseEvent | TouchEvent,
    ) {
        if (!selectedCreative || !canvasRef) return;
        if (which === "logo" && (!showLogo || !logoImage)) return;
        if (which === "qr" && !showQr) return;
        if (which === "extra" && (!showExtra || !extraImage)) return;

        e.preventDefault();

        dragging = null;
        resizing = which;

        const p = getClientPoint(e);
        dragStartClientX = p.x;
        dragStartClientY = p.y;

        resizeStartSize =
            which === "qr" ? qrSize : which === "logo" ? logoSize : extraSize;
    }

    function shouldResizeFromEvent(e: MouseEvent | TouchEvent): boolean {
        const target = e.currentTarget as HTMLElement | null;
        if (!target) return false;

        const rect = target.getBoundingClientRect();
        const p = getClientPoint(e);

        const cornerSize = 26;
        const withinX = p.x >= rect.right - cornerSize;
        const withinY = p.y >= rect.bottom - cornerSize;
        return withinX && withinY;
    }

    function startMoveOrResize(
        which: "qr" | "logo" | "extra",
        e: MouseEvent | TouchEvent,
    ) {
        if (shouldResizeFromEvent(e)) {
            startResize(which, e);
            return;
        }

        if (which === "extra") {
            startDragExtra(e);
        } else {
            startDrag(which, e);
        }
    }

    function schedulePreviewRender() {
        if (previewRaf) return;
        previewRaf = window.requestAnimationFrame(() => {
            previewRaf = 0;
            renderPreview();
        });
    }

    function computeZoom() {
        if (!selectedCreative || !previewRef) return;

        const availableW = Math.max(1, previewRef.clientWidth - 20);
        const availableH = Math.max(1, previewRef.clientHeight - 20);

        const fitW = availableW / Math.max(1, selectedCreative.width);
        const fitH = availableH / Math.max(1, selectedCreative.height);

        fitZoom = clamp(Math.min(fitW, fitH), 0.1, 2);

        // Auto: fit to container (maximize size while keeping full image visible)
        if (zoomAuto) {
            zoom = fitZoom;
            manualZoom = zoom;
        } else {
            zoom = clamp(manualZoom, 0.1, 2);
        }
    }

    $effect(() => {
        if (typeof window === "undefined") return;
        if (!previewRef) return;

        computeZoom();
        const ro = new ResizeObserver(() => computeZoom());
        ro.observe(previewRef);

        return () => ro.disconnect();
    });

    $effect(() => {
        if (step !== "edit") return;
        computeZoom();
    });

    function handleDrag(e: MouseEvent | TouchEvent) {
        if ((!dragging && !resizing) || !selectedCreative || !canvasRef) return;

        const rect = canvasRef.getBoundingClientRect();
        const scaleX = selectedCreative.width / Math.max(1, rect.width);
        const scaleY = selectedCreative.height / Math.max(1, rect.height);

        const p = getClientPoint(e);
        const dx = (p.x - dragStartClientX) * scaleX;
        const dy = (p.y - dragStartClientY) * scaleY;

        if (resizing) {
            const shortSide = Math.min(
                selectedCreative.width,
                selectedCreative.height,
            );
            const delta = Math.max(dx, dy);
            const nextSize = clamp(resizeStartSize + delta, 16, shortSide);
            const nextPercent = (nextSize / Math.max(1, shortSide)) * 100;

            if (resizing === "qr") {
                qrSizePercent = clamp(nextPercent, 10, 40);
            } else if (resizing === "logo") {
                logoSizePercent = clamp(nextPercent, 5, 30);
            } else {
                extraSizePercent = clamp(nextPercent, 5, 40);
            }

            schedulePreviewRender();
            return;
        }

        if (dragging === "qr") {
            qrX = clamp(dragStartX + dx, 0, selectedCreative.width - qrSize);
            qrY = clamp(dragStartY + dy, 0, selectedCreative.height - qrSize);
        } else if (dragging === "logo") {
            logoX = clamp(
                dragStartX + dx,
                0,
                selectedCreative.width - logoSize,
            );
            logoY = clamp(dragStartY + dy, 0, selectedCreative.height - logoH);
        } else {
            extraX = clamp(
                dragStartX + dx,
                0,
                selectedCreative.width - extraSize,
            );
            extraY = clamp(
                dragStartY + dy,
                0,
                selectedCreative.height - extraH,
            );
        }

        schedulePreviewRender();
    }

    function endDrag() {
        dragging = null;
        resizing = null;
    }

    $effect(() => {
        if (typeof window === "undefined") return;

        window.addEventListener("mousemove", handleDrag as any);
        window.addEventListener("mouseup", endDrag);
        window.addEventListener(
            "touchmove",
            handleDrag as any,
            {
                passive: false,
            } as any,
        );
        window.addEventListener("touchend", endDrag);

        return () => {
            window.removeEventListener("mousemove", handleDrag as any);
            window.removeEventListener("mouseup", endDrag);
            window.removeEventListener("touchmove", handleDrag as any);
            window.removeEventListener("touchend", endDrag);

            if (previewRaf) {
                window.cancelAnimationFrame(previewRaf);
                previewRaf = 0;
            }
        };
    });

    function renderPreview() {
        if (!canvasRef || !selectedCreative || !baseImage || !qrImage) return;

        syncSizesAndClamp();

        const overlays: OverlayElement[] = [];
        if (showQr) {
            overlays.push({
                image: qrImage,
                x: qrX,
                y: qrY,
                width: qrSize,
                height: qrSize,
                type: "qr",
            });
        }
        if (showLogo && logoImage) {
            overlays.push({
                image: logoImage,
                x: logoX,
                y: logoY,
                width: logoSize,
                height: logoH,
                type: "logo",
            });
        }
        if (showExtra && extraImage) {
            overlays.push({
                image: extraImage,
                x: extraX,
                y: extraY,
                width: extraSize,
                height: extraH,
                type: "extra",
            });
        }

        const composed = renderComposition({
            baseImage,
            overlays,
            width: selectedCreative.width,
            height: selectedCreative.height,
        });

        canvasRef.width = composed.width;
        canvasRef.height = composed.height;
        const ctx = canvasRef.getContext("2d");
        ctx?.clearRect(0, 0, canvasRef.width, canvasRef.height);
        ctx?.drawImage(composed, 0, 0);
    }

    $effect(() => {
        if (step === "edit") renderPreview();
    });

    async function selectCreative(creative: Creative) {
        loading = true;
        error = "";
        selectedCreative = creative;

        try {
            baseImage = await loadImage(creative.src);

            // Always use the real image dimensions to avoid distortion
            if (baseImage) {
                selectedCreative = {
                    ...creative,
                    width: baseImage.naturalWidth || creative.width,
                    height: baseImage.naturalHeight || creative.height,
                };
                creative = selectedCreative;
            }

            const qrDataUrl = await generateQrDataUrl(referralLink, {
                size: 512,
                errorCorrectionLevel: "H",
            });
            qrImage = await loadImage(qrDataUrl);

            logoImage = null;
            showLogo = false;
            if (partnerLogo) {
                try {
                    logoImage = await loadImage(partnerLogo);
                    showLogo = true;
                } catch {
                    logoImage = null;
                    showLogo = false;
                }
            }

            qrSize = sizeFromPercent(qrSizePercent, creative);
            const qrPos = getDefaultQrPosition(
                creative.width,
                creative.height,
                qrSize,
            );
            qrX = qrPos.x;
            qrY = qrPos.y;

            logoSize = sizeFromPercent(logoSizePercent, creative);
            logoH = computeHeight(logoSize, logoImage);
            const logoPos = getDefaultLogoPosition(
                creative.width,
                creative.height,
                logoSize,
                logoH,
            );
            logoX = logoPos.x;
            logoY = logoPos.y;

            if (selectedCreative) {
                extraSize = sizeFromPercent(extraSizePercent, creative);
                extraH = computeHeight(extraSize, extraImage);
                const padding = 48;
                extraX = clamp(padding, 0, creative.width - extraSize);
                extraY = clamp(padding, 0, creative.height - extraH);
            }

            step = "edit";
        } catch (e) {
            console.error(e);
            error = translate(
                "LANG_CREATIVE_LOAD_ERROR",
                "No se pudo cargar la creatividad",
            );
        } finally {
            loading = false;
        }
    }

    function backToSelector() {
        step = "select";
        selectedCreative = null;
        baseImage = null;
        qrImage = null;
        logoImage = null;
        extraImage = null;
        showExtra = false;
    }

    async function download(type: "image/png" | "image/jpeg") {
        if (!selectedCreative || !baseImage || !qrImage) return;
        loading = true;
        error = "";

        try {
            syncSizesAndClamp();

            const overlays: OverlayElement[] = [];
            if (showQr) {
                overlays.push({
                    image: qrImage,
                    x: qrX,
                    y: qrY,
                    width: qrSize,
                    height: qrSize,
                    type: "qr",
                });
            }
            if (showLogo && logoImage) {
                overlays.push({
                    image: logoImage,
                    x: logoX,
                    y: logoY,
                    width: logoSize,
                    height: logoH,
                    type: "logo",
                });
            }
            if (showExtra && extraImage) {
                overlays.push({
                    image: extraImage,
                    x: extraX,
                    y: extraY,
                    width: extraSize,
                    height: extraH,
                    type: "extra",
                });
            }

            const composed = renderComposition({
                baseImage,
                overlays,
                width: selectedCreative.width,
                height: selectedCreative.height,
            });

            const filename = `caloo_partner_${referralCode || "creative"}_${selectedCreative.id}`;
            await exportCanvas(composed, {
                type,
                filename,
                quality: 0.92,
            });
        } catch (e) {
            console.error(e);
            error = translate(
                "LANG_CREATIVE_EXPORT_ERROR",
                "Error al exportar la imagen",
            );
        } finally {
            loading = false;
        }
    }

    function percentLeft(x: number) {
        return `${((x / Math.max(1, selectedCreative?.width || 1)) * 100).toFixed(4)}%`;
    }
    function percentTop(y: number) {
        return `${((y / Math.max(1, selectedCreative?.height || 1)) * 100).toFixed(4)}%`;
    }
    function percentWidth(s: number) {
        return `${((s / Math.max(1, selectedCreative?.width || 1)) * 100).toFixed(4)}%`;
    }
    function percentHeight(s: number) {
        return `${((s / Math.max(1, selectedCreative?.height || 1)) * 100).toFixed(4)}%`;
    }
</script>

{#snippet content()}
    <div class={`creativeGen creativeGen--${step}`}>
        {#if loading}
            <div class="creativeGen__loading">
                <Spinner text={""} />
            </div>
        {/if}

        {#if error}
            <div class="creativeGen__error">{error}</div>
        {/if}

        {#if step === "select"}
            <div class="creativeGen__topActions">
                <input
                    class="creativeGen__fileInput"
                    bind:this={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onchange={handleFiles}
                />
                <button type="button" class="--cancel" onclick={openFilePicker}>
                    {translate("LANG_CREATIVE_UPLOAD_IMAGE", "Subir imagen")}
                </button>

                <div class="creativeGen__lang">
                    <label for="creativeLang">
                        {translate("LANG_CREATIVE_LANGUAGE", "Idioma")}
                    </label>
                    <select
                        id="creativeLang"
                        class="creativeGen__langSelect"
                        bind:value={creativeLanguage}
                    >
                        {#each supportedCreativeLanguages as l (l)}
                            <option value={l}>{l.toUpperCase()}</option>
                        {/each}
                    </select>
                </div>
            </div>

            <p class="creativeGen__hint">
                {translate(
                    "LANG_CREATIVE_SELECT_DESCRIPTION",
                    "Selecciona una imagen base para tu creatividad",
                )}
            </p>

            <div class="creativeGen__grid">
                {#each selectionCreatives as creative (creative.id)}
                    <button
                        type="button"
                        class="creativeGen__card"
                        onclick={() => selectCreative(creative)}
                    >
                        <img
                            class="creativeGen__thumb"
                            src={encodeURI(creative.src)}
                            alt={creative.label}
                            loading="lazy"
                            onerror={() => hideMissingTemplate(creative.src)}
                        />
                        <div class="creativeGen__meta">
                            <div class="creativeGen__title">
                                {creative.label}
                                <span class="creativeGen__icon">
                                    {getFormatIcon(creative.recommendedFormat)}
                                </span>
                            </div>
                            <div class="creativeGen__sub">
                                {creative.width} × {creative.height}
                            </div>
                        </div>
                    </button>
                {/each}
            </div>
        {:else}
            <div class="creativeGen__editor">
                <div
                    class={`creativeGen__preview ${!zoomAuto && manualZoom > fitZoom ? "creativeGen__preview--scroll" : ""}`}
                    bind:this={previewRef}
                >
                    <div class="creativeGen__zoom">
                        {translate("LANG_CREATIVE_ZOOM", "Zoom")}: {Math.round(
                            zoom * 100,
                        )}%
                    </div>

                    <div
                        class="creativeGen__stage"
                        style="--stage-w: {selectedCreative?.width ||
                            1}px; --stage-h: {selectedCreative?.height ||
                            1}px; --zoom: {zoom};"
                    >
                        <canvas
                            bind:this={canvasRef}
                            class="creativeGen__canvas"
                        ></canvas>

                        {#if selectedCreative && showQr}
                            <button
                                type="button"
                                class="creativeGen__handle creativeGen__handle--qr"
                                aria-label={translate(
                                    "LANG_CREATIVE_DRAG_QR",
                                    "Mover QR",
                                )}
                                style="left: {percentLeft(
                                    qrX,
                                )}; top: {percentTop(
                                    qrY,
                                )}; width: {percentWidth(
                                    qrSize,
                                )}; height: {percentHeight(qrSize)};"
                                onmousedown={(e) => startMoveOrResize("qr", e)}
                                ontouchstart={(e) => startMoveOrResize("qr", e)}
                            ></button>
                        {/if}

                        {#if selectedCreative && showLogo && logoImage}
                            <button
                                type="button"
                                class="creativeGen__handle creativeGen__handle--logo"
                                aria-label={translate(
                                    "LANG_CREATIVE_DRAG_LOGO",
                                    "Mover logo",
                                )}
                                style="left: {percentLeft(
                                    logoX,
                                )}; top: {percentTop(
                                    logoY,
                                )}; width: {percentWidth(
                                    logoSize,
                                )}; height: {percentHeight(logoH)};"
                                onmousedown={(e) =>
                                    startMoveOrResize("logo", e)}
                                ontouchstart={(e) =>
                                    startMoveOrResize("logo", e)}
                            ></button>
                        {/if}

                        {#if selectedCreative && showExtra && extraImage}
                            <button
                                type="button"
                                class="creativeGen__handle creativeGen__handle--extra"
                                aria-label={translate(
                                    "LANG_CREATIVE_DRAG_EXTRA",
                                    "Mover imagen extra",
                                )}
                                style="left: {percentLeft(
                                    extraX,
                                )}; top: {percentTop(
                                    extraY,
                                )}; width: {percentWidth(
                                    extraSize,
                                )}; height: {percentHeight(extraH)};"
                                onmousedown={(e) =>
                                    startMoveOrResize("extra", e)}
                                ontouchstart={(e) =>
                                    startMoveOrResize("extra", e)}
                            ></button>
                        {/if}
                    </div>
                </div>

                <div class="creativeGen__controls">
                    <div class="creativeGen__row">
                        <label>
                            <input
                                type="checkbox"
                                bind:checked={zoomAuto}
                                onchange={() => computeZoom()}
                            />
                            {translate(
                                "LANG_CREATIVE_ZOOM_AUTO",
                                "Zoom automático (ajustar)",
                            )}
                        </label>
                    </div>
                    <div class="creativeGen__row">
                        <label for="zoomRange">
                            {translate("LANG_CREATIVE_ZOOM", "Zoom")}: {Math.round(
                                manualZoom * 100,
                            )}%
                        </label>
                        <input
                            id="zoomRange"
                            type="range"
                            min="10"
                            max="200"
                            value={Math.round(manualZoom * 100)}
                            oninput={(e) => {
                                const v = Number(
                                    (e.currentTarget as HTMLInputElement).value,
                                );
                                manualZoom = clamp(v / 100, 0.1, 2);
                                zoomAuto = false;
                                computeZoom();
                            }}
                        />
                    </div>

                    <div class="creativeGen__row">
                        <label>
                            <input
                                type="checkbox"
                                bind:checked={showQr}
                                onchange={renderPreview}
                            />
                            {translate("LANG_CREATIVE_SHOW_QR", "Mostrar QR")}
                        </label>
                    </div>
                    <div class="creativeGen__row">
                        <label for="qrSizeRange">
                            {translate("LANG_CREATIVE_QR_SIZE", "Tamaño QR")}: {qrSizePercent}%
                        </label>
                        <input
                            id="qrSizeRange"
                            type="range"
                            min="10"
                            max="40"
                            bind:value={qrSizePercent}
                            oninput={renderPreview}
                        />
                    </div>

                    <div class="creativeGen__row">
                        <label>
                            <input
                                type="checkbox"
                                bind:checked={showLogo}
                                disabled={!logoImage}
                                onchange={renderPreview}
                            />
                            {translate(
                                "LANG_CREATIVE_SHOW_LOGO",
                                "Mostrar logo",
                            )}
                        </label>
                    </div>
                    <div class="creativeGen__row">
                        <label for="logoSizeRange">
                            {translate(
                                "LANG_CREATIVE_LOGO_SIZE",
                                "Tamaño logo",
                            )}: {logoSizePercent}%
                        </label>
                        <input
                            id="logoSizeRange"
                            type="range"
                            min="5"
                            max="30"
                            bind:value={logoSizePercent}
                            oninput={renderPreview}
                            disabled={!logoImage || !showLogo}
                        />
                    </div>

                    <div class="creativeGen__row creativeGen__row--inline">
                        <input
                            class="creativeGen__fileInput"
                            bind:this={extraInputRef}
                            type="file"
                            accept="image/*"
                            onchange={handleExtraFile}
                        />
                        <button
                            type="button"
                            class="--cancel"
                            onclick={openExtraPicker}
                        >
                            {translate(
                                "LANG_CREATIVE_UPLOAD_EXTRA_IMAGE",
                                "Subir imagen extra",
                            )}
                        </button>
                        <label>
                            <input
                                type="checkbox"
                                bind:checked={showExtra}
                                disabled={!extraImage}
                                onchange={renderPreview}
                            />
                            {translate(
                                "LANG_CREATIVE_SHOW_EXTRA_IMAGE",
                                "Mostrar imagen extra",
                            )}
                        </label>
                    </div>

                    <div class="creativeGen__row">
                        <label for="extraSizeRange">
                            {translate(
                                "LANG_CREATIVE_EXTRA_SIZE",
                                "Tamaño imagen extra",
                            )}: {extraSizePercent}%
                        </label>
                        <input
                            id="extraSizeRange"
                            type="range"
                            min="5"
                            max="40"
                            bind:value={extraSizePercent}
                            oninput={renderPreview}
                            disabled={!extraImage || !showExtra}
                        />
                    </div>

                    <div class="creativeGen__actions">
                        <button
                            type="button"
                            class="--cancel"
                            onclick={backToSelector}
                        >
                            {translate(
                                "LANG_CREATIVE_BACK",
                                "Cambiar creatividad",
                            )}
                        </button>
                        <button
                            type="button"
                            class="--primary"
                            onclick={() => download("image/png")}
                        >
                            {translate(
                                "LANG_CREATIVE_DOWNLOAD_PNG",
                                "Descargar PNG",
                            )}
                        </button>
                        <button
                            type="button"
                            class="--primaryBorder"
                            onclick={() => download("image/jpeg")}
                        >
                            {translate(
                                "LANG_CREATIVE_DOWNLOAD_JPG",
                                "Descargar JPG",
                            )}
                        </button>
                    </div>
                </div>
            </div>
        {/if}
    </div>
{/snippet}

{#snippet infoButton()}{/snippet}

<Modal
    isModalOpen={true}
    centered={false}
    className={`creativeGenModal ${step === "select" ? "creativeGenModal--select" : "creativeGenModal--edit"}`}
    title={translate("LANG_CREATIVE_GENERATOR_TITLE", "Generar creatividades")}
    {onClose}
    {infoButton}
    onAccept={() => {}}
    {content}
/>

<style lang="scss">
    @import "./styles/index.scss";
</style>
